import { useState, useEffect } from "react";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { api } from "@/lib/api";

export default function LocationSelect({
  state: stateProp,
  district: districtProp,
  onStateChange,
  onDistrictChange,
  stateRequired = false,
  districtRequired = false,
  className,
  stateClassName,
  districtClassName,
  label,
  inline = false,
}) {
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [stateOpen, setStateOpen] = useState(false);
  const [districtOpen, setDistrictOpen] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  useEffect(() => {
    api.get("/locations/states")
      .then(({ data }) => setStates(data.states || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!stateProp) {
      setDistricts([]);
      return;
    }
    setLoadingDistricts(true);
    api.get(`/locations/districts?state=${encodeURIComponent(stateProp)}`)
      .then(({ data }) => setDistricts(data.districts || []))
      .catch(() => setDistricts([]))
      .finally(() => setLoadingDistricts(false));
  }, [stateProp]);

  const handleStateChange = (val) => {
    const newVal = val === stateProp ? "" : val;
    onStateChange(newVal);
  };

  const stateBtn = (
    <Popover open={stateOpen} onOpenChange={setStateOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={stateOpen}
          className={cn(
            "h-12 justify-between rounded-full font-normal text-sm px-4",
            inline ? "w-full min-w-[180px] sm:w-[210px]" : "w-full",
            !stateProp && "text-muted-foreground",
            stateClassName
          )}
        >
          <span className="flex min-w-0 items-center gap-2 truncate">
            <MapPin size={14} className="shrink-0 text-muted-foreground" />
            <span className="truncate">{stateProp || "State"}</span>
          </span>
          <ChevronsUpDown size={14} className="shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0">
        <Command>
          <CommandInput placeholder="Search states..." />
          <CommandList>
            <CommandEmpty>No state found.</CommandEmpty>
            <CommandGroup>
              {states.map((s) => (
                <CommandItem
                  key={s}
                  value={s}
                  onSelect={(current) => {
                    handleStateChange(current);
                    setStateOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", stateProp === s ? "opacity-100" : "opacity-0")}
                  />
                  {s}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  const districtBtn = (
    <Popover
      open={districtOpen}
      onOpenChange={setDistrictOpen}
      disabled={!stateProp}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={districtOpen}
          disabled={!stateProp}
          className={cn(
            "h-12 justify-between rounded-full font-normal text-sm px-4",
            inline ? "w-full min-w-[180px] sm:w-[210px]" : "w-full",
            (!districtProp || !stateProp) && "text-muted-foreground",
            districtClassName
          )}
        >
          <span className="flex min-w-0 items-center gap-2 truncate">
            <MapPin size={14} className="shrink-0 text-muted-foreground" />
            <span className="truncate">{loadingDistricts ? "Loading..." : districtProp || "District"}</span>
          </span>
          <ChevronsUpDown size={14} className="shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0">
        <Command>
          <CommandInput placeholder="Search districts..." />
          <CommandList>
            <CommandEmpty>No district found.</CommandEmpty>
            <CommandGroup>
              {districts.map((d) => (
                <CommandItem
                  key={d}
                  value={d}
                  onSelect={(current) => {
                    onDistrictChange(current === districtProp ? "" : current);
                    setDistrictOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", districtProp === d ? "opacity-100" : "opacity-0")}
                  />
                  {d}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  if (inline) {
    return (
      <div className={cn("grid w-full grid-cols-1 gap-2 sm:w-auto sm:grid-cols-2", className)}>
        {stateBtn}
        {districtBtn}
      </div>
    );
  }

  return (
    <div className={cn("grid grid-cols-2 gap-3", className)}>
      {label && (
        <div className="col-span-2 mb-1">
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
      )}
      <div>
        {stateBtn}
        {stateRequired && !stateProp && (
          <span className="text-[11px] text-destructive mt-0.5 block">Required</span>
        )}
      </div>
      <div>
        {districtBtn}
        {districtRequired && stateProp && !districtProp && (
          <span className="text-[11px] text-destructive mt-0.5 block">Required</span>
        )}
      </div>
    </div>
  );
}
